import { useState } from "react";
import { EmployeeDetails } from "./PayslipForm";

export interface HistoryEntry {
  id: string;
  createdAt: number;
  details: EmployeeDetails;
  letterhead: string | null;
   signature: string | null;
  primaryColor: string;
  contentMargin: number;
  signaturePadding: number;
}

interface PayslipHistoryProps {
  history: HistoryEntry[];
  onLoad: (entry: HistoryEntry) => void;
  onDelete: (id: string) => void;
}

export const PayslipHistory = ({ history, onLoad, onDelete }: PayslipHistoryProps) => {
  // State to track which ID is being deleted (null = no modal open)
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // 1. Function called when user clicks "Delete" in table
  const initiateDelete = (id: string) => {
    setDeleteId(id); // This opens the modal
  };

  // 2. Function called when user clicks "Yes, Delete" in modal
  const confirmDelete = () => {
    if (deleteId) {
      onDelete(deleteId); // Call the actual delete function
      setDeleteId(null);  // Close modal
    }
  };

  // 3. Function called when user clicks "Cancel"
  const cancelDelete = () => {
    setDeleteId(null); // Close modal without deleting
  };

  if (history.length === 0) {
    return (
      <div className="history-card empty-state">
        <div className="empty-icon">📂</div>
        <h3>No History Yet</h3>
        <p>Generated payslips will appear here after you download them.</p>
      </div>
    );
  }

  return (
    <>
      <div className="history-card">
        <div className="history-header">
          <h2>Recent History</h2>
          <span className="history-badge">Auto-Saved on Download</span>
        </div>

        <div className="history-table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Letterhead</th>
                <th>Employee</th>
                <th>Pay Period</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id}>
                  <td className="date-cell">{formatDate(entry.createdAt)}</td>
                  <td>
                    {entry.letterhead ? (
                      <img 
                        src={entry.letterhead} 
                        alt="Brand" 
                        className="history-thumbnail"
                      />
                    ) : (
                      <span className="no-img">No Image</span>
                    )}
                  </td>
                  <td className="font-medium">{entry.details.employeeName}</td>
                  <td>{entry.details.payPeriod}</td>
                  <td className="text-right">
                    <div className="action-buttons">
                      <button 
                        onClick={() => onLoad(entry)} 
                        className="btn-action btn-load"
                      >
                        Load
                      </button>
                      {/* Changed onClick to open modal instead of window.confirm */}
                      <button 
                        onClick={() => initiateDelete(entry.id)} 
                        className="btn-action btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- CUSTOM MODAL --- */}
      {deleteId && (
        <div className="modal-overlay" onClick={cancelDelete}>
          {/* stopPropagation prevents closing when clicking inside the box */}
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this payslip record? 
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-modal btn-modal-cancel" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button 
                className="btn-modal btn-modal-delete" 
                onClick={confirmDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};